import type { PickReadonly } from "@suse-o11y/pick-readonly";
import { nanoid } from "nanoid";

import { type Setter, resolve } from "../../generic/setter";

import { DashboardLayoutSpec, type DashboardLayoutSpecInitConfig } from "./_dashboard-layout-spec";
import { DashboardWidgetSpec, type DashboardWidgetSpecInitConfig } from "./_dashboard-widget-spec";

export type DashboardPanelSpecConfig = PickReadonly<Omit<DashboardPanelSpec, "update" | "isEqual">>;

export class DashboardPanelSpec {
	public static fromWidget(widget: DashboardWidgetSpecInitConfig): DashboardPanelSpec {
		return new DashboardPanelSpec(
			nanoid(),
			DashboardLayoutSpec.of(DashboardPanelSpec.getLayoutSpecForWidget(widget)),
			DashboardWidgetSpec.of(widget),
		);
	}

	private static getLayoutSpecForWidget(widget: DashboardWidgetSpecInitConfig): DashboardLayoutSpecInitConfig {
		switch (widget.plugin.kind) {
			case "TimeSeriesChart":
			case "Markdown": {
				return { width: 8, height: 2 };
			}

			case "StatChart":
			case "GaugeChart":
			case "PieChart": {
				return { width: 6, height: 2 };
			}

			default:
				return { width: 4, height: 2 };
		}
	}

	public constructor(
		public readonly id: string,
		public readonly layout: DashboardLayoutSpec,
		public readonly widget: DashboardWidgetSpec,
	) {}

	public update(setter: Setter<Partial<DashboardPanelSpecConfig>, [DashboardPanelSpec]>): DashboardPanelSpec {
		const { id = this.id, layout = this.layout, widget = this.widget } = resolve(setter, this);

		const updated = new DashboardPanelSpec(id, layout, widget);

		return this.isEqual(updated) ? this : updated;
	}

	public isEqual(another: DashboardPanelSpec): boolean {
		if (this === another) {
			return true;
		}

		return this.id === another.id && this.layout.isEqual(another.layout) && this.widget.isEqual(another.widget);
	}
}
